import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { cardsShadow } from '../../../styleHelpers/mixins/shadow';

const TOOLTIP_NATURAL_WIDTH = 300;

export enum EToolTipTheme {
    DARK = 'DARK',
    LIGHT = 'LIGHT'
}

export enum ETooltipArrowDirection {
    NONE = 'NONE',
    TOP = 'TOP',
    BOTTOM = 'BOTTOM',
    RIGHT = 'RIGHT',
    LEFT = 'LEFT'
}

const Wrapper = styled.div<{ colorsSchema: EToolTipTheme, width?: number, top: number, left: number }>`
    background: ${colorStack.content};
    position: fixed;
    ${fontSizeAndHeight[16]};
    ${cardsShadow};
    border-radius: 8px;
    z-index: 999999;
    padding: .5rem 1rem;
    max-width: 496px;
    ${({width}) => width && css`
        max-width: ${`${width}px`};
    `}
    white-space: normal;
    display: inline-flex;
    flex-direction: column;
    top: 0;
    left: 0;
    ${props => props.colorsSchema === EToolTipTheme.LIGHT && css`
        background: ${colorStack.whiteGrey};
    `}
    ${({top}) => top != null && css`
        top: ${`${top}px`};
    `}
    ${({left}) => left != null && css`
        left: ${`${left}px`};
    `}
`;

const Triangle = styled.div<{ colorsSchema: EToolTipTheme, direction: ETooltipArrowDirection }>`
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    border-top: 10px solid transparent;
    border-right: 8px solid transparent;
    border-left: 8px solid transparent;
    border-bottom: 10px solid ${colorStack.content};
    bottom: 100%;
    width: 16px;
    ${props => props.colorsSchema === EToolTipTheme.LIGHT && css`
        border-bottom: 10px solid ${colorStack.whiteGrey};
    `}
    ${props => props.direction === ETooltipArrowDirection.NONE && css`
        display: none;
    `}
    ${props => props.direction === ETooltipArrowDirection.BOTTOM && css`
        bottom: -20px;
        border-top: 10px solid ${props.colorsSchema === EToolTipTheme.DARK ? colorStack.content : colorStack.whiteGrey};
        border-right: 8px solid transparent;
        border-left: 8px solid transparent;
        border-bottom: 10px solid transparent;
    `}
    ${props => props.direction === ETooltipArrowDirection.LEFT && css`
        left: -20px;
        margin: 0;
        right: auto;
        bottom: auto;
        top: calc((100% / 2) - 8px);
        border-top: 8px solid transparent;
        border-right: 10px solid ${props.colorsSchema === EToolTipTheme.DARK ? colorStack.content : colorStack.whiteGrey};
        border-left: 10px solid transparent;
        border-bottom: 8px solid transparent;
    `}
    ${props => props.direction === ETooltipArrowDirection.RIGHT && css`
        right: -20px;
        margin: 0;
        left: auto;
        bottom: auto;
        top: calc((100% / 2) - 8px);
        border-top: 8px solid transparent;
        border-right: 10px solid transparent;
        border-left: 10px solid ${props.colorsSchema === EToolTipTheme.DARK ? colorStack.content : colorStack.whiteGrey};
        border-bottom: 8px solid transparent;
    `}
`;

const Title = styled.pre<{ numberOfLines: number, colorsSchema: EToolTipTheme }>`
    width: 100%;
    display: block;
    margin: 0;
    overflow: hidden;
    color: ${colorStack.white}!important;
    font-weight: 500;
    word-wrap: break-word;
    white-space: pre-wrap;
    ${props => props.colorsSchema === EToolTipTheme.LIGHT && css`
        color: ${colorStack.content}!important;
        font-weight: 400;
    `}
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    white-space: nowrap;
    line-height: 1.1rem;
    ${props => props.numberOfLines > 1 && css`
        display: -webkit-box !important;
        white-space: normal;
        -webkit-line-clamp: ${props.numberOfLines};
    `}
`;

const Content = styled.span<{ colorsSchema: EToolTipTheme }>`
    color: ${colorStack.disabled}!important;
`;

interface IProps {
    theme?: EToolTipTheme;
    fixedWidth?: number;
    arrowDirection?: ETooltipArrowDirection;
    title: string | React.ReactNode;
    content?: string | React.ReactNode;
    top?: number;
    left?: number;
    setTooltipWidth?(width: number);
    setTooltipHeight?(height: number);
}

export const ToolTip: FC<React.PropsWithChildren<IProps>> = props => {
    const wrapperRef = useRef<HTMLDivElement>();
    const addictionalSafeSpace = 20;
    const [contentWidth, setContentWidth] = useState<number>(0);
    const lastReportedWidthRef = useRef<number>(0);
    const lastReportedHeightRef = useRef<number>(0);
    const setTooltipWidthRef = useRef(props.setTooltipWidth);
    const setTooltipHeightRef = useRef(props.setTooltipHeight);

    useLayoutEffect(() => {
        setTooltipWidthRef.current = props.setTooltipWidth;
        setTooltipHeightRef.current = props.setTooltipHeight;
    }, [props.setTooltipWidth, props.setTooltipHeight]);

    useLayoutEffect(() => {
        if (props.fixedWidth) {
            if (lastReportedWidthRef.current !== props.fixedWidth) {
                lastReportedWidthRef.current = props.fixedWidth;
                setContentWidth(props.fixedWidth);
                setTooltipWidthRef.current?.(props.fixedWidth);
            }
        } else {
            const node = wrapperRef?.current;
            const measured = Math.ceil(((node?.scrollWidth || node?.clientWidth || 0)) + addictionalSafeSpace);
            const nextWidth = measured > 0 ? measured : TOOLTIP_NATURAL_WIDTH;
            if (lastReportedWidthRef.current !== nextWidth) {
                lastReportedWidthRef.current = nextWidth;
                setContentWidth(nextWidth);
                setTooltipWidthRef.current?.(nextWidth);
            }
        }

        const node = wrapperRef?.current;
        if (node) {
            const height = node.offsetHeight || node.clientHeight || 0;
            if (height > 0 && lastReportedHeightRef.current !== height) {
                lastReportedHeightRef.current = height;
                setTooltipHeightRef.current?.(height);
            }
        }
    }, [props.content, props.title, props.fixedWidth]);

    const finalLeft = Math.max(10, props.left ?? 0);
    const finalTop = props.top ?? 0;

    return ReactDOM.createPortal(
        <Wrapper ref={wrapperRef} colorsSchema={props.theme || EToolTipTheme.DARK} width={Number(props?.fixedWidth) || contentWidth} top={finalTop} left={finalLeft}>
            <Triangle colorsSchema={props.theme || EToolTipTheme.DARK} direction={props.arrowDirection || ETooltipArrowDirection.TOP} />
            <Title numberOfLines={8} colorsSchema={props.theme || EToolTipTheme.DARK}>{props.title}</Title>
            <Content colorsSchema={props.theme || EToolTipTheme.DARK}>{props.content}</Content>
        </Wrapper>, document.getElementById('tooltip-root')
    );
};